import React, { useEffect, useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Checkbox } from './Checkbox';
import { ChampEtiquete } from './ChampEtiquete';
import { EnteteSection } from './EnteteSection';
import { MonthYearField } from './MonthYearField';
import { X } from './icons';
import { formatMonthYear } from '../utils/monthYear';
import { QuantifyHint } from './QuantifyHint';
import { colors, formes, shadow, spacing, typography } from '../theme/tokens';
import type { ExperienceEntry, MonthYear } from '../types';
import { ongoingLabel } from '../utils/documentLabels';
import type { OutputLanguage } from '../types';
import { t } from '../i18n';

const EMPTY_MY: MonthYear = { month: '', year: '' };

function emptyEntry(): ExperienceEntry {
  return { role: '', company: '', start: { ...EMPTY_MY }, end: { ...EMPTY_MY }, current: false, description: '' };
}

// Sérialise les expériences en texte étiqueté avec dates réelles (entrées vides omises).
export function serializeExperiences(
  entries: ExperienceEntry[],
  // Langue du DOCUMENT : « En cours » est une valeur écrite dans le CV, pas un libellé.
  outputLanguage: OutputLanguage = 'fr',
): string {
  return entries
    .map((e) => {
      const header = [e.role.trim(), e.company.trim()].filter(Boolean).join(' - ');
      const start = formatMonthYear(e.start);
      const end = e.current ? ongoingLabel(outputLanguage) : formatMonthYear(e.end);
      const range = [start, end].filter(Boolean).join(' – ');
      const head = range ? `${header} (${range})` : header;
      const desc = e.description.trim();
      if (!header && !desc) return '';
      return desc ? `${head} : ${desc}` : head;
    })
    .filter(Boolean)
    .join('\n');
}

interface ExperienceEditorProps {
  value: ExperienceEntry[];
  onChange: (entries: ExperienceEntry[]) => void;
  testID?: string;
}

export function ExperienceEditor({ value, onChange, testID }: ExperienceEditorProps) {
  const update = (index: number, patch: Partial<ExperienceEntry>) =>
    onChange(value.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  // Rang de la ligne qui vient d'être ajoutée : son premier champ prend le focus à l'apparition,
  // sans quoi une ligne créée sous le bas de l'écran passerait inaperçue.
  const nouvelle = useRef(-1);
  // Une seule fois : sans cela, une ligne remontée plus tard à ce rang reprendrait le focus.
  useEffect(() => {
    nouvelle.current = -1;
  });

  return (
    <View testID={testID} style={styles.container}>
      <EnteteSection
        titre={t('editors.experience.titre')}
        ajouterA11y={t('editors.experience.add')}
        ajouterTestID="exp-add"
        onAjouter={() => {
          nouvelle.current = value.length;
          onChange([...value, emptyEntry()]);
        }}
      />

      {value.map((entry, index) => (
        <View key={index} testID={`exp-row-${index}`} style={styles.row}>
          <View style={styles.rowHeader}>
            <Text style={styles.rowTitle}>{t('editors.experience.rowTitle', { n: index + 1 })}</Text>
            <Pressable
              testID={`exp-remove-${index}`}
              accessibilityRole="button"
              accessibilityLabel={t('a11y.removeExperience')}
              onPress={() => remove(index)}
              style={styles.remove}
            >
              <X size={18} color={colors.grey500} />
            </Pressable>
          </View>

          <View style={styles.duo}>
            <View style={styles.moitie}>
              <ChampEtiquete
                testID={`exp-role-${index}`}
            autoFocus={index === nouvelle.current}
                label={t('editors.experience.roleLabel')}
                value={entry.role}
                onChangeText={(role) => update(index, { role })}
              />
            </View>
            <View style={styles.moitie}>
              <ChampEtiquete
                testID={`exp-company-${index}`}
                label={t('editors.experience.company')}
                value={entry.company}
                onChangeText={(company) => update(index, { company })}
              />
            </View>
          </View>

          <Text style={styles.miniLabel}>{t('editors.experience.start')}</Text>
          <MonthYearField
            value={entry.start}
            onChange={(start) => update(index, { start })}
            testID={`exp-start-${index}`}
          />

          <Checkbox
            label={t('editors.experience.current')}
            checked={entry.current}
            onToggle={(current) => update(index, { current })}
            testID={`exp-current-${index}`}
          />

          {!entry.current ? (
            <>
              <Text style={styles.miniLabel}>{t('editors.experience.end')}</Text>
              <MonthYearField
                value={entry.end}
                onChange={(end) => update(index, { end })}
                testID={`exp-end-${index}`}
              />
            </>
          ) : null}

          <ChampEtiquete
            testID={`exp-desc-${index}`}
            label={t('editors.experience.descriptionLabel')}
            placeholder={t('editors.experience.descriptionPlaceholder')}
            multiline
            value={entry.description}
            onChangeText={(description) => update(index, { description })}
          />
        </View>
      ))}

      {/* Une seule fois sous la liste : répété à chaque expérience, le conseil devenait du bruit. */}
      {value.length > 0 ? <QuantifyHint /> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  row: {
    gap: spacing.sm,
    backgroundColor: colors.white,
    borderRadius: formes.rayon.md,
    padding: spacing.md,
    ...shadow.card,
  },
  rowHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  rowTitle: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  remove: { minWidth: formes.cible, minHeight: formes.cible, alignItems: 'flex-end', justifyContent: 'center' },
  duo: { flexDirection: 'row', gap: spacing.sm },
  moitie: { flex: 1, minWidth: 0 },
  miniLabel: {
    fontSize: typography.caption.size,
    fontWeight: '700',
    color: colors.grey700,
  },
});
